import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import {
  createNewDatasetV2,
  fetchDatasetPreviews,
  fetchFolderMetadata,
  selectDatasetFolders,
  startFolderUploadTransaction,
  startMultipleFolderUploadTransaction,
} from "redux/CreateDataset/actions";
import useFolderValidation from "./useFolderValidation";
import { Form } from "antd";

export default function useCreateDataset(batchId?: string) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const { validateFolders, validateFoldersPaths } =
    useFolderValidation(batchId);

  const createDatasetHandler = async (name?: string) => {
    // Use new workflow action creator
    return await dispatch(createNewDatasetV2() as any);
  };

  /**
   * `resetForm: false` keeps whatever is typed in the path form - for folders added from
   * elsewhere (scan-result exports) while the user may be mid-way through a path.
   */
  const startFoldersPathsHandler = async (
    paths: string[],
    { resetForm = true }: { resetForm?: boolean } = {},
  ) => {
    await validateFoldersPaths(paths);
    if (resetForm) form.resetFields();
    // Awaited so callers can tell when the added folders are listed, e.g. to replace a
    // placeholder row with the real one without a gap.
    await dispatch(fetchFolderMetadata(newDatasetId!) as any);
  };

  const startFolderUpload = async (files: File[]) => {
    try {
      // Validate folders using the validation hook
      const validationResults = await validateFolders(files);

      if (!validationResults) {
        return; // Validation failed
      }

      const { validFolders } = validationResults;

      // Only proceed with valid folders
      if (validFolders.length === 0) {
        return;
      }

      if (validFolders.length === 1) {
        // Single folder scenario (browser selection OR single drag&drop folder)
        const folderGroup = validFolders[0];
        await dispatch(
          startFolderUploadTransaction(
            newDatasetId!,
            folderGroup.folderName,
            folderGroup.files,
            batchId,
          ) as any,
        );
      } else {
        // Multiple folders scenario (only possible via drag&drop)
        await dispatch(
          startMultipleFolderUploadTransaction(
            newDatasetId!,
            validFolders,
            batchId,
          ) as any,
        );
      }
    } catch (error: any) {
      let errorMessage: string =
        error.response?.data?.message ||
        "There was an error starting the folder upload transaction";
      console.error(errorMessage);
    }
  };

  const selectFolders = async (folderIds: string[], batchId?: string) => {
    if (!newDatasetId) {
      throw new Error("No dataset ID available");
    }

    return await dispatch(
      selectDatasetFolders(newDatasetId, folderIds, batchId) as any,
    );
  };

  const fetchPreviews = async () => {
    return await dispatch(fetchDatasetPreviews(newDatasetId!, batchId) as any);
  };

  return {
    createNewDataset: createDatasetHandler,
    startFolderUpload,
    selectFolders,
    fetchPreviews,
    startFoldersPathsHandler,
    form,
  };
}
